import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Search, Filter, UserPlus, Users, Check, X, UserMinus } from 'lucide-react';
import { PatientAssignmentModal } from './PatientAssignmentModal';
import { useCareTeams } from '../../lib/hooks/useCareTeams';
import { useHospitals } from '../../lib/hooks/useHospitals';
import { useUnassignPatient } from '../../lib/hooks/usePatients';
import { LoadingState } from '../common/LoadingState';
import { AlertCard } from '../common/AlertCard';

// Type definitions
interface Patient {
  id: string | number;
  name: string;
  age: number;
  status: string;
  care_team_id?: number | null;
  has_care_team?: boolean;
  hospital_id?: number;
}

interface PatientAssignmentScreenProps {
  patients: Patient[];
  assignments?: any[]; // Legacy, not used anymore
  careTeams?: any[];
  nurses?: any[];
  physiotherapists?: any[];
  doctors?: any[];
  onAssign: (assignment: { patient_id: number; care_team_id: number }) => void;
}

export function PatientAssignmentScreen({
  patients = [],
  assignments = [], // Legacy parameter, kept for compatibility
  careTeams: propCareTeams = [],
  nurses = [],
  physiotherapists = [],
  doctors = [],
  onAssign,
}: PatientAssignmentScreenProps) {
  // Fetch hospitals and care teams from API
  const { data: hospitalsData, isLoading: hospitalsLoading, error: hospitalsError } = useHospitals();
  const { data: careTeamsData, isLoading: careTeamsLoading, error: careTeamsError } = useCareTeams();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignDialog, setShowUnassignDialog] = useState(false);
  const [patientToUnassign, setPatientToUnassign] = useState<Patient | null>(null);

  // Mutation hook for unassigning
  const unassignMutation = useUnassignPatient();
  
  // Use fetched care teams or fallback to props
  const careTeams = careTeamsData?.care_teams || propCareTeams || [];
  const hospitals = hospitalsData || []; // ✅ Fixed: hospitalsData is already the array, not an object

  // Filter patients
  const filteredPatients = (patients || []).filter(patient => {
    const matchesSearch = patient.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || patient.status === statusFilter;
    
    // ✅ Use patient.care_team_id directly instead of separate assignments array
    const isAssigned = patient.care_team_id !== null && patient.care_team_id !== undefined;
    const matchesAssignment =
      assignmentFilter === 'all' ||
      (assignmentFilter === 'assigned' && isAssigned) ||
      (assignmentFilter === 'unassigned' && !isAssigned);

    return matchesSearch && matchesStatus && matchesAssignment;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'On Track':
      case 'active':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'Missed':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'Escalated':
      case 'critical':
        return 'bg-destructive/10 text-destructive border-destructive';
      default:
        return '';
    }
  };

  const handleAssignClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowAssignModal(true);
  };

  const handleAssignComplete = (assignment: { patient_id: number; care_team_id: number }) => {
    onAssign(assignment);
    setShowAssignModal(false);
    setSelectedPatient(null);
  };

  const handleUnassignClick = (patient: Patient) => {
    setPatientToUnassign(patient);
    setShowUnassignDialog(true);
  };

  const handleUnassignConfirm = () => {
    if (patientToUnassign) {
      const patientId = typeof patientToUnassign.id === 'string' 
        ? parseInt(patientToUnassign.id) 
        : patientToUnassign.id;
      
      unassignMutation.mutate(patientId, {
        onSuccess: () => {
          setShowUnassignDialog(false);
          setPatientToUnassign(null);
        },
      });
    }
  };

  // ✅ Count unassigned using care_team_id
  const unassignedCount = patients.filter(
    p => p.care_team_id === null || p.care_team_id === undefined
  ).length;

  // ✅ Count assigned using care_team_id
  const assignedCount = patients.filter(
    p => p.care_team_id !== null && p.care_team_id !== undefined
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-primary text-xl sm:text-2xl">Patient Assignment</h2>
        <p className="text-muted-foreground text-sm">
          Assign patients to care teams. All team members will automatically have access to the patient.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-[#D4F0F8] border-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Patients</p>
                <p className="text-2xl text-primary mt-1">{patients.length}</p>
              </div>
              <Users className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#14CAF4]/10 border-[#14CAF4]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Assigned</p>
                <p className="text-2xl text-[#14CAF4] mt-1">{assignedCount}</p>
              </div>
              <Check className="w-8 h-8 text-[#14CAF4]" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#F07D13]/10 border-[#F07D13]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Unassigned</p>
                <p className="text-2xl text-[#F07D13] mt-1">{unassignedCount}</p>
              </div>
              <X className="w-8 h-8 text-[#F07D13]" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#2762AC]/10 border-[#2762AC]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Care Teams</p>
                <p className="text-2xl text-[#2762AC] mt-1">{careTeams.length}</p>
              </div>
              <Users className="w-8 h-8 text-[#2762AC]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="shadow-lg bg-[#E5E5E3]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[160px] bg-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="On Track">On Track</SelectItem>
                <SelectItem value="Missed">Missed</SelectItem>
                <SelectItem value="Escalated">Escalated</SelectItem>
              </SelectContent>
            </Select>
            <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
              <SelectTrigger className="w-full sm:w-[160px] bg-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Assignment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Patients</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Patient List */}
      <Card className="shadow-lg">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-primary flex items-center gap-2">
            <Users className="w-5 h-5" />
            Patient List ({filteredPatients.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {filteredPatients.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No patients found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPatients.map(patient => {
                // ✅ Get care team from patient's care_team_id
                const careTeam = patient.care_team_id
                  ? careTeams.find((ct: any) => ct.id === patient.care_team_id || ct.id?.toString() === patient.care_team_id?.toString())
                  : null;
                
                const isAssigned = patient.care_team_id !== null && patient.care_team_id !== undefined;

                return (
                  <div
                    key={patient.id}
                    className="p-4 border border-border rounded-xl hover:border-primary/50 transition-all bg-white"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      {/* Patient Info */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-foreground">
                            {patient.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{patient.name}</p>
                          <p className="text-sm text-muted-foreground">{patient.age} years</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={getStatusColor(patient.status)}>
                              {patient.status}
                            </Badge>
                            {!isAssigned && (
                              <Badge className="bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]">
                                Unassigned
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Assignment Info */}
                      {isAssigned && careTeam ? (
                        <div className="flex-1 space-y-2 text-sm bg-[#E5E5E3] p-3 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Users className="w-4 h-4 text-[#2762AC] flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground">Care Team</p>
                              <p className="truncate font-medium">{careTeam.name}</p>
                            </div>
                          </div>
                          {careTeam.hospital && (
                            <div className="flex items-start gap-2">
                              <div className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground">Hospital</p>
                                <p className="truncate">{careTeam.hospital.name || careTeam.hospital_name}</p>
                              </div>
                            </div>
                          )}
                          {careTeam.members && careTeam.members.length > 0 && (
                            <div className="flex items-start gap-2">
                              <div className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground">Team Members</p>
                                <p className="truncate text-xs">{careTeam.members.length} members</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : isAssigned ? (
                        <div className="flex-1 space-y-2 text-sm bg-[#E5E5E3] p-3 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Users className="w-4 h-4 text-[#2762AC] flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground">Care Team</p>
                              <p className="truncate">Assigned (ID: {patient.care_team_id})</p>
                              <p className="text-xs text-muted-foreground">Team details loading...</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 flex items-center justify-center p-6 border-2 border-dashed border-[#F07D13]/30 rounded-lg">
                          <p className="text-sm text-muted-foreground">No assignment yet</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-shrink-0 gap-2">
                        <Button
                          onClick={() => handleAssignClick(patient)}
                          className={
                            isAssigned
                              ? 'bg-[#2762AC] hover:bg-[#2762AC]/90'
                              : 'bg-[#F07D13] hover:bg-[#F07D13]/90'
                          }
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          {isAssigned ? 'Reassign' : 'Assign'}
                        </Button>
                        {isAssigned && (
                          <Button
                            variant="outline"
                            onClick={() => handleUnassignClick(patient)}
                            className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            disabled={unassignMutation.isPending}
                          >
                            <UserMinus className="w-4 h-4 mr-2" />
                            Unassign
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assignment Modal */}
      {selectedPatient && (
        <PatientAssignmentModal
          open={showAssignModal}
          onOpenChange={setShowAssignModal}
          patient={selectedPatient}
          currentAssignment={
            selectedPatient.care_team_id
              ? { care_team_id: selectedPatient.care_team_id }
              : undefined
          }
          hospitals={hospitals}
          careTeams={careTeams}
          onAssign={handleAssignComplete}
          isReassignment={selectedPatient.care_team_id !== null && selectedPatient.care_team_id !== undefined}
        />
      )}

      {/* Unassign Dialog */}
      <AlertDialog open={showUnassignDialog} onOpenChange={setShowUnassignDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unassign Patient</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unassign this patient from their care team?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnassignConfirm}>
              <UserMinus className="w-4 h-4 mr-2" />
              Unassign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}